/**
 * Converts Markdown to HTML for email sending.
 * Produces clean, email-safe HTML.
 * Handles all common markdown patterns.
 */

interface MarkdownRule {
  pattern: RegExp;
  replacement: string | ((match: string, ...groups: string[]) => string);
}

const MARKDOWN_RULES: MarkdownRule[] = [
  // Bold with ** (must be processed before italic to avoid conflicts)
  { pattern: /\*\*(.+?)\*\*/g, replacement: '<strong>$1</strong>' },
  // Bold with __
  { pattern: /__(.+?)__/g, replacement: '<strong>$1</strong>' },
  // Italic with * (single asterisk - bold is already converted, so no conflict)
  { pattern: /\*([^*\n]+)\*/g, replacement: '<em>$1</em>' },
  // Italic with _ (simplified - matches _text_ with word boundaries)
  { pattern: /\b_([^_\n]+)_\b/g, replacement: '<em>$1</em>' },
  // Underline (++text++ extended markdown syntax)
  { pattern: /\+\+(.+?)\+\+/g, replacement: '<u>$1</u>' },
  // Strikethrough
  { pattern: /~~(.+?)~~/g, replacement: '<del>$1</del>' },
  // Inline code
  { pattern: /`([^`]+)`/g, replacement: '<code>$1</code>' },
  // Images (must be before links to avoid ![alt](url) being consumed by link rule)
  { pattern: /!\[([^\]]*)\]\(([^)]+)\)/g, replacement: '<img src="$2" alt="$1" />' },
  // Links
  { pattern: /\[([^\]]+)\]\(([^)]+)\)/g, replacement: '<a href="$2">$1</a>' },
  // Headings (must be processed in order from h6 to h1)
  { pattern: /^###### (.+)$/gm, replacement: '<h6>$1</h6>' },
  { pattern: /^##### (.+)$/gm, replacement: '<h5>$1</h5>' },
  { pattern: /^#### (.+)$/gm, replacement: '<h4>$1</h4>' },
  { pattern: /^### (.+)$/gm, replacement: '<h3>$1</h3>' },
  { pattern: /^## (.+)$/gm, replacement: '<h2>$1</h2>' },
  { pattern: /^# (.+)$/gm, replacement: '<h1>$1</h1>' },
  // Horizontal rule
  { pattern: /^(---|\*\*\*|___)$/gm, replacement: '<hr>' },
];

const convertLists = (text: string): string => {
  // Convert unordered lists (supports - and * with optional leading whitespace)
  // Match consecutive lines that start with - or * followed by space
  const ulPattern = /(?:^[ \t]*[-*][ \t]+.+$\n?)+/gm;
  text = text.replace(ulPattern, (match) => {
    const items = match
      .split('\n')
      .filter((line) => (/^[ \t]*[-*][ \t]+/).test(line))
      .map((line) => {
        const content = line.replace(/^[ \t]*[-*][ \t]+/, '');
        return `<li>${content}</li>`;
      })
      .join('');
    // Preserve trailing newline so subsequent list patterns can anchor with ^
    return `<ul>${items}</ul>\n`;
  });

  // Convert ordered lists (supports numbers with . or ) followed by space)
  const olPattern = /(?:^[ \t]*\d+[.)]\s+.+$\n?)+/gm;
  text = text.replace(olPattern, (match) => {
    const items = match
      .split('\n')
      .filter((line) => (/^[ \t]*\d+[.)]\s+/).test(line))
      .map((line) => {
        const content = line.replace(/^[ \t]*\d+[.)]\s+/, '');
        return `<li>${content}</li>`;
      })
      .join('');
    return `<ol>${items}</ol>\n`;
  });

  return text;
};

const convertBlockquotes = (text: string): string => {
  // Match consecutive lines starting with >
  const blockquotePattern = /(?:^>[ \t]?.*$\n?)+/gm;
  return text.replace(blockquotePattern, (match) => {
    const content = match
      .split('\n')
      .map((line) => line.replace(/^>[ \t]?/, ''))
      .join('<br>');
    return `<blockquote>${content}</blockquote>`;
  });
};

const convertParagraphs = (text: string): string => {
  return text
    .split('\n\n')
    .map((para) => para.trim())
    .filter(Boolean)
    .map((para) => {
      // Don't wrap if already a block element
      if ((/^<(h[1-6]|ul|ol|blockquote|hr|p)/).test(para)) {
        return para;
      }
      return `<p>${para.replace(/\n/g, '<br>')}</p>`;
    })
    .join('\n');
};

export const markdownToHtml = (markdown: string): string => {
  let result = markdown;

  // Normalize line endings
  result = result.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Convert blockquotes first (before other processing)
  result = convertBlockquotes(result);

  // Convert lists before applying inline rules
  result = convertLists(result);

  // Apply inline markdown rules
  for (const rule of MARKDOWN_RULES) {
    result = result.replace(rule.pattern, rule.replacement as string);
  }

  // Convert paragraphs last
  result = convertParagraphs(result);

  return result;
};
